import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  getCandidateAssessment,
  submitCandidateAssessment,
} from "@/api/assessments";
import { Loader } from "./ui/loader";
import { AssessmentTask } from "@/types";
import {
  saveAssessmentProgress,
  loadAssessmentProgress,
  clearAssessmentProgress,
} from "@/utils/localStorage";
import { ClockIcon, TimerIcon } from "@radix-ui/react-icons";
import { PlayIcon } from "lucide-react";
import { formatElapsedTime } from "@/utils/time";

export function TakeAssessment() {
  const { candidateId } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  const [assessment, setAssessment] = useState<AssessmentTask | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [started, setStarted] = useState(false);
  const [assessmentStatus, setAssessmentStatus] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState("00:00:00");

  const fetchAssessment = useCallback(
    async (startAssessment = false) => {
      if (!candidateId || !email) {
        setError("Candidate ID and email are required");
        return;
      }

      try {
        setLoading(true);
        const response = await getCandidateAssessment(
          candidateId,
          email,
          startAssessment
        );
        setAssessment(response);

        // Check if the assessment has already been started
        const status = response.progress?.status || "pending";
        const hasStarted = status === "in_progress";
        setStarted(hasStarted);
        setAssessmentStatus(status);

        // If the assessment is already completed, set submitted to true
        if (status === "completed") {
          setSubmitted(true);
        }

        // Try to load saved state from localStorage if the assessment has been started
        if (hasStarted && response.assessment?.id) {
          const savedState = loadAssessmentProgress(
            candidateId,
            response.assessment.id
          );
          if (savedState) {
            setAnswers(savedState.answers);
            setCurrentQuestionIndex(savedState.currentQuestionIndex);
          } else if (response.progress?.answers) {
            // If no saved state but answers exist in the response, use those
            setAnswers(response.progress.answers);
          }
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load assessment"
        );
      } finally {
        setLoading(false);
      }
    },
    [candidateId, email]
  );

  useEffect(() => {
    if (candidateId && email) {
      fetchAssessment(false); // Don't start the assessment yet, just check status
    }
  }, [candidateId, email, fetchAssessment]);

  // Save state to localStorage whenever answers or currentQuestionIndex changes
  useEffect(() => {
    if (assessment && candidateId && !submitted && started) {
      saveAssessmentProgress(
        candidateId,
        answers,
        currentQuestionIndex,
        assessment.assessment?.id
      );
    }
  }, [
    answers,
    currentQuestionIndex,
    assessment,
    candidateId,
    submitted,
    started,
  ]);

  // Update the timer every second when the assessment is in progress
  useEffect(() => {
    if (!started || submitted || !assessment?.progress?.started_at) return;

    // Initial update
    setElapsedTime(formatElapsedTime(assessment.progress.started_at));

    // Set up interval to update the timer every second
    const timerInterval = setInterval(() => {
      setElapsedTime(formatElapsedTime(assessment.progress.started_at));
    }, 1000);

    // Clean up the interval when the component unmounts or when the assessment is submitted
    return () => clearInterval(timerInterval);
  }, [started, submitted, assessment]);

  // Function to start the assessment
  const handleStartAssessment = async () => {
    try {
      await fetchAssessment(true); // Start the assessment
      setStarted(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to start assessment"
      );
    }
  };

  const handleAnswer = (answer: string) => {
    if (!assessment || !assessment.assessment?.questions) return;

    setAnswers({
      ...answers,
      [assessment.assessment?.questions[currentQuestionIndex].id]: answer,
    });
  };

  const handleNext = () => {
    if (!assessment) return;
    if (currentQuestionIndex < assessment.assessment?.questions_count - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (!candidateId || !email) return;

    try {
      setLoading(true);
      const response = await submitCandidateAssessment(
        candidateId,
        email,
        answers
      );

      if (response.success) {
        setSubmitted(true);
        // Clear localStorage data on successful submission
        if (candidateId) {
          clearAssessmentProgress(candidateId);
        }
      } else {
        setError("Failed to submit assessment");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to submit assessment"
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  if (error) return <div className="text-red-500 p-4">{error}</div>;
  if (!assessment) return <div className="p-4">Assessment not found</div>;

  // If the assessment is paused, show a message
  if (assessment.assessment?.status === "paused") {
    return (
      <div className="container mx-auto p-4 max-w-3xl">
        <Card className="p-6 text-center">
          <div className="flex justify-center mb-4">
            <ClockIcon className="h-12 w-12 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-yellow-600 mb-4">
            Assessment paused
          </h1>
          <p className="mb-6">
            This assessment is currently paused by the administrator. Please
            check back later.
          </p>
        </Card>
      </div>
    );
  }

  if (assessment.assessment?.status !== "published") {
    return (
      <div className="container mx-auto p-4 max-w-3xl">
        <Card className="p-6 text-center">
          <div className="flex justify-center mb-4">
            <ClockIcon className="h-12 w-12 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-yellow-600 mb-4">
            Assessment not published
          </h1>
          <p className="mb-6">
            The assessment you are trying to take has not been published yet.
          </p>
        </Card>
      </div>
    );
  }

  // If the assessment has been submitted
  if (submitted) {
    return (
      <div className="container mx-auto p-4 max-w-3xl">
        <Card className="p-6 text-center">
          <h1 className="text-2xl font-bold text-green-600 mb-4">
            Assessment submitted!
          </h1>
          <p className="mb-6">
            Thank you for completing this assessment. Your responses have been
            recorded.
          </p>
        </Card>
      </div>
    );
  }

  // If the assessment hasn't been started yet, show the start screen
  if (!started) {
    return (
      <div className="container mx-auto p-4 max-w-3xl">
        <Card className="p-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">
              {assessment.assessment?.title}
            </h1>
            <p className="text-gray-600 mb-6">
              {assessment.assessment?.job_specification?.title}
            </p>

            <div className="bg-blue-50 p-4 rounded-lg mb-6 max-w-xl mx-auto">
              <h2 className="font-semibold mb-2">Before you begin:</h2>
              <ul className="text-left list-disc pl-5 space-y-2">
                <li>
                  This assessment contains{" "}
                  {assessment.assessment?.questions_count} questions
                </li>
                <li>
                  You can navigate between questions using the Previous and Next
                  buttons
                </li>
                <li>Your progress will be automatically saved</li>
                <li>
                  Once you start, you can return to complete the assessment
                  later if needed
                </li>
              </ul>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={handleStartAssessment}
                size="lg"
                className="mt-4"
                icon={<PlayIcon className="h-4 w-4" />}
              >
                {assessmentStatus === "in_progress"
                  ? "Resume assessment"
                  : "Start assessment"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const currentQuestion =
    assessment.assessment?.questions?.[currentQuestionIndex];

  return (
    <div className="container mx-auto p-4 max-w-3xl">
      <Card className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">
            {assessment.assessment?.title}
          </h1>
          <p className="text-gray-600">
            {assessment.assessment?.job_specification?.title}
          </p>
          <div className="flex items-center justify-center mt-4 text-sm font-medium text-gray-500">
            <TimerIcon className="h-4 w-4 mr-1" />
            <span>Time elapsed: {elapsedTime}</span>
          </div>
        </div>

        <div className="mb-8">
          <div className="text-sm text-gray-500 mb-2">
            Question {currentQuestionIndex + 1} of{" "}
            {assessment.assessment?.questions_count}
          </div>

          <h2 className="text-xl mb-4">{currentQuestion?.text}</h2>

          {currentQuestion?.type === "multiple-choice" &&
            currentQuestion?.options && (
              <div className="space-y-2">
                {currentQuestion.options.map((option, index) => (
                  <div key={index} className="flex items-center">
                    <input
                      type="radio"
                      id={`option-${index}`}
                      name={`question-${currentQuestion.id}`}
                      value={option}
                      checked={answers[currentQuestion.id] === option}
                      onChange={(e) => handleAnswer(e.target.value)}
                      className="mr-2"
                    />
                    <label htmlFor={`option-${index}`}>{option}</label>
                  </div>
                ))}
              </div>
            )}

          {currentQuestion?.type === "open-ended" && (
            <textarea
              value={answers[currentQuestion?.id] || ""}
              onChange={(e) => handleAnswer(e.target.value)}
              className="w-full p-2 border rounded-md"
              rows={4}
            />
          )}
        </div>

        <div className="flex justify-between">
          <Button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            variant="outline"
          >
            Previous
          </Button>

          {currentQuestionIndex ===
          assessment.assessment?.questions_count - 1 ? (
            <Button onClick={handleSubmit}>Submit</Button>
          ) : (
            <Button onClick={handleNext}>Next</Button>
          )}
        </div>
      </Card>
    </div>
  );
}
